import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";

/**
 * ImageCropDialog — modal that lets you crop an image to a target aspect ratio,
 * returning a Blob. Accepts either a local `file` (fresh upload) or a `src` URL
 * (an image already stored on the server). Lightweight wrapper around react-easy-crop.
 */
export default function ImageCropDialog({ file, src, aspect = 3 / 4, title, onCancel, onConfirm }) {
  const [imageSrc] = useState(() => (file ? URL.createObjectURL(file) : src));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Release the object URL when the dialog unmounts, whichever way it closes.
  useEffect(() => () => { if (file) URL.revokeObjectURL(imageSrc); }, [file, imageSrc]);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    setError("");
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, file?.type || "image/jpeg");
      if (!blob) throw new Error("Empty crop");
      onConfirm(blob);
    } catch (err) {
      // Most likely a tainted canvas (image loaded cross-origin without CORS).
      setError(err?.message || "Could not process this crop.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-melilux-bg/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-6" data-testid="crop-dialog">
      <div className="w-full max-w-3xl max-h-[92vh] bg-melilux-bg border border-white/15 flex flex-col">
        <div className="shrink-0 px-6 py-4 border-b border-white/10">
          <p className="overline">Crop image</p>
          <h3 className="font-serif text-xl md:text-2xl mt-1">{title || "Frame the piece."}</h3>
        </div>

        {/* Grows to fill whatever height is left, so the controls below stay on screen. */}
        <div className="relative flex-1 min-h-[220px] bg-black">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid={true}
            objectFit="contain"
          />
        </div>

        <div className="shrink-0 px-6 py-4 border-t border-white/10">
          <label className="text-[0.65rem] uppercase tracking-[0.28em] text-melilux-cream/55 block mb-2">
            Zoom
          </label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-melilux-gold"
            data-testid="crop-zoom"
          />
          {error && (
            <p className="mt-3 text-[0.7rem] text-destructive" data-testid="crop-error">{error}</p>
          )}
          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              data-testid="crop-cancel"
              className="px-6 py-3 text-[0.7rem] uppercase tracking-[0.28em] border border-white/15 hover:border-melilux-cream/40 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              data-testid="crop-confirm"
              className="px-7 py-3 text-[0.7rem] uppercase tracking-[0.28em] bg-melilux-gold text-melilux-bg hover:bg-melilux-goldBright transition-colors disabled:opacity-60"
            >
              {busy ? "Processing…" : "Apply crop"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.crossOrigin = "anonymous";
    img.src = url;
  });
}

async function getCroppedBlob(imageSrc, pixelCrop, mimeType) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType === "image/png" ? "image/png" : "image/jpeg", 0.92);
  });
}
